import * as React from 'react'
import styles from './PostPreviewText.pcss'
import { buildHTMLWithTags } from 'utils/composer'
import { sanitizeComposerHtml } from 'utils/sanitize'
import { SocialProfileTag } from 'shared/types'
import { BRAND_FACEBOOK } from 'shared'

interface PostPreviewTextProps {
  text: string
  tags?: SocialProfileTag[]
  network?: string
  maxLinesDefault: number
  expandLabel: string
  collapseLabel: string
  className: string
  color?: string
  backgroundImageUrl?: string
}

export const PostPreviewText = React.memo((props: PostPreviewTextProps) => {
  const [expanded, setExpanded] = React.useState(false)
  const containerRef = React.useRef<HTMLDivElement>(null)
  const [showToggle, setShowToggle] = React.useState(false)

  React.useEffect(() => {
    if (!containerRef.current) {
      return
    }
    // NOTE: `props.text` is the post's plain-text status, but it is written with `innerHTML`
    // so the browser reparses it as markup. It can carry anything that reached the composer -
    // the `shareUrl` query param, a third-party feed title, a draft reloaded from the API -
    // so it has to be sanitized here even though it is nominally "just text".
    if (props.network === BRAND_FACEBOOK && props.tags && containerRef.current) {
      // NOTE: buildHTMLWithTags sanitizes its own return value
      containerRef.current.innerHTML = buildHTMLWithTags(props.text, props.tags, props.network)
    } else {
      containerRef.current.innerHTML = sanitizeComposerHtml(props.text)
    }
  }, [props.tags, props.text, props.network])

  React.useEffect(() => {
    if (!containerRef.current) {
      return
    }

    const withToggle = containerRef.current.clientHeight < containerRef.current.scrollHeight
    setShowToggle(withToggle)
  }, [props.text])

  const trimStyle = expanded ? {} : { '--line-clamp': props.maxLinesDefault } as React.CSSProperties

  const toggle = () => {
    setExpanded(current => !current)
  }

  const style: any = props.backgroundImageUrl ? { backgroundImage: `url(${props.backgroundImageUrl})` } : undefined
  if (props.color) {
    style.color = props.color
  }

  return (
    <div
      data-testid="post-preview-text"
      className={`${props.className} ${props.backgroundImageUrl ? styles['txt-bg'] : ''} ${expanded ? styles.expanded : ''}`}
      style={style}
    >
      <div>
        <div className={styles.trim} style={trimStyle} ref={containerRef}></div>
        {(showToggle || expanded) && (
          <div className={styles.actions}>
            <span className={styles.btn} onClick={toggle}>
              {expanded ? props.collapseLabel : props.expandLabel}
            </span>
          </div>
        )}
      </div>
    </div>
  )
})
export default PostPreviewText
